import React, { useState, useEffect, useRef } from "react";
import { X, ChevronDown, Check, AlertCircle } from "lucide-react";
import api from "~/services/api";
import { toast } from "react-toastify";

const Toast = ({ message, show, onClose }) => {
  useEffect(() => {
    if (show) {
      const timer = setTimeout(onClose, 3000);
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);

  if (!show) return null;

  return (
    <div className="fixed top-5 left-1/2 transform -translate-x-1/2 z-[60] animate-in fade-in slide-in-from-top-5 duration-300">
      <div className="bg-red-600 text-white px-6 py-3 rounded-lg shadow-xl flex items-center gap-3">
        <AlertCircle className="w-5 h-5" />
        <span className="font-medium text-sm">{message}</span>
        <button onClick={onClose} className="ml-2 hover:bg-red-700 p-1 rounded-full">
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

const SearchableSelect = ({
  label,
  options,
  value,
  onChange,
  placeholder = "Select an option",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState(
    options.find((opt) => opt === value)?.toString() ||
      options.find((opt) => opt.value === value)?.label ||
      options.find((opt) => opt.label === value)?.label ||
      value ||
      ""
  );
  const containerRef = useRef(null);

  useEffect(() => {
    const next =
      options.find((opt) => opt === value)?.toString() ||
      options.find((opt) => opt.value === value)?.label ||
      options.find((opt) => opt.label === value)?.label ||
      value ||
      "";
    setQuery(next);
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const normalizedOptions = options.map((opt) =>
    typeof opt === "string" ? { value: opt, label: opt } : opt
  );

  const filteredOptions = normalizedOptions.filter((opt) =>
    opt.label.toLowerCase().includes((query || "").toLowerCase())
  );

  const handleSelect = (option) => {
    setQuery(option.label);
    onChange(option.value);
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    onChange(e.target.value);
    setIsOpen(true);
  };

  return (
    <div className="flex flex-col gap-1.5" ref={containerRef}>
      <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">
        {label}
      </label>

      <div className="relative">
        <input
          type="text"
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-500 pr-8"
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          <ChevronDown
            size={16}
            className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
          />
        </button>

        {isOpen && (
          <div className="absolute z-50 mt-1 w-full bg-white shadow-xl max-h-48 rounded-md py-1 text-sm overflow-auto border border-gray-200 top-full left-0">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-2 text-gray-500 italic">No options found</div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelect(option)}
                  className={`w-full text-left px-4 py-2 hover:bg-orange-50 flex items-center justify-between ${
                    option.value === value
                      ? "bg-orange-50 text-orange-700 font-medium"
                      : "text-gray-700"
                  }`}
                >
                  {option.label}
                  {option.value === value && <Check size={14} />}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default function ModalFilters({
  opened = false,
  onClose = () => null,
  applying = false,
  onApplyFilters = () => null,
}) {
  const [formData, setFormData] = useState({
    department: "",
    status: "",
    type: "",
    periodFrom: "",
    periodTo: "",
  });

  const [toastState, setToastState] = useState({ show: false, message: "" });
  const [isClosing, setIsClosing] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [scopeInfo, setScopeInfo] = useState(null);
  const [loadingScope, setLoadingScope] = useState(false);

  const statuses = [
    { value: "draft", label: "Draft" },
    { value: "submitted", label: "Submitted" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
  ];

  const types = [
    { value: "Faculty", label: "Faculty" },
    { value: "Staff", label: "Staff" },
  ];

  useEffect(() => {
    async function loadScope() {
      if (!opened) return;
      try {
        setLoadingScope(true);
        const { data } = await api.get("/timesheets/scope");
        setScopeInfo(data || null);
      } catch (err) {
        console.error("Error loading timesheet scope:", err);
        toast.error("Failed to load access scope");
      } finally {
        setLoadingScope(false);
      }
    }
    loadScope();
  }, [opened]);

  useEffect(() => {
    if (scopeInfo?.role === "self" || scopeInfo?.role === "leader") {
      setFormData((prev) => ({ ...prev, department: "" }));
    }
    if (scopeInfo?.role === "self") {
      setFormData((prev) => ({ ...prev, type: "" }));
    }
  }, [scopeInfo]);

  useEffect(() => {
    async function loadDepartments() {
      if (!opened) return;
      if (scopeInfo?.role === "self" || scopeInfo?.role === "leader") return;
      try {
        const { data } = await api.get("/departments", {
          params: { limit: 999, page: 1, orderBy: "name", orderASC: "ASC" },
        });
        const options = (data?.rows || data || []).map((item) => ({
          value: item.id,
          label: item.name,
        }));
        setDepartments(options);
      } catch (err) {
        console.error("Error loading departments:", err);
        toast.error("Failed to load departments list");
      }
    }
    loadDepartments();
  }, [opened, scopeInfo]);

  const buttonBaseClasses =
    "px-4 py-2 text-sm font-medium border rounded-lg transition-colors shadow-xs flex-1 justify-center items-center flex";
  const secondaryButtonClasses = `${buttonBaseClasses} text-gray-700 bg-white border-gray-300 hover:bg-gray-50`;
  const primaryButtonClasses = `${buttonBaseClasses} text-gray-700 bg-white border-gray-300 hover:bg-gray-50`;
  const clearButtonClasses = "w-full py-2 text-sm font-medium text-red-600 bg-white border border-red-100 rounded-lg hover:bg-red-50 transition-colors mb-1";

  const showDepartment = scopeInfo?.role !== "self" && scopeInfo?.role !== "leader";
  const showType = scopeInfo?.role !== "self";

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const handleApplyFilters = async () => {
    const searchableFields = [
      { key: "department", label: "Department", options: departments },
      { key: "status", label: "Status", options: statuses },
      { key: "type", label: "Type", options: types },
    ];

    const invalidField = searchableFields.find((field) => {
      const value = formData[field.key];
      if (!value) return false;
      if (Array.isArray(field.options)) {
        return !field.options.some(
          (opt) =>
            (typeof opt === "string" && opt === value) ||
            (typeof opt === "object" && opt.value === value)
        );
      }
      return false;
    });

    if (invalidField) {
      setToastState({
        show: true,
        message: `Option Invalid, choose a option for ${invalidField.label}`
      });
      return;
    }

    console.log("Filters to apply:", formData);
    await onApplyFilters(formData);
    handleClose();
  };

  if (!opened) return null;

  return (
    <>
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes slideOutRight {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(100%);
            opacity: 0;
          }
        }

        .modal-slide-in {
          animation: slideInRight 0.3s ease-out forwards;
        }

        .modal-slide-out {
          animation: slideOutRight 0.3s ease-in forwards;
        }
      `}</style>

      <Toast
        show={toastState.show}
        message={toastState.message}
        onClose={() => setToastState({ ...toastState, show: false })}
      />

      <div
        className="fixed inset-0 z-50 flex justify-end items-center py-6"
        style={{ backgroundColor: "rgba(0,0,0,.75)" }}
        onClick={(event) => {
          if (event.target === event.currentTarget) handleClose();
        }}
      >
        <div className={`bg-white w-full max-w-md h-full flex flex-col shadow-2xl overflow-hidden rounded-l-2xl ${
          isClosing ? 'modal-slide-out' : 'modal-slide-in'
        }`}>

          <div className="flex items-center justify-between px-6 py-6 border-b border-gray-200 bg-white shrink-0">
            <div className="flex flex-col gap-1">
              <h2 className="text-lg font-bold text-gray-900 uppercase tracking-tight">Filters</h2>
              <p className="text-xs text-gray-500 font-medium">
                Refine your search results
              </p>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="p-2 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors"
              aria-label="Close filters"
            >
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-6 text-sm text-gray-700 custom-scrollbar">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-5">
                {showDepartment && (
                  <SearchableSelect
                    label="Department"
                    options={departments}
                    value={formData.department}
                    onChange={(val) => handleChange("department", val)}
                    placeholder="Select department..."
                  />
                )}

                <SearchableSelect
                  label="Status"
                  options={statuses}
                  value={formData.status}
                  onChange={(val) => handleChange("status", val)}
                  placeholder="Select status..."
                />

                {showType && (
                  <SearchableSelect
                    label="Type"
                    options={types}
                    value={formData.type}
                    onChange={(val) => handleChange("type", val)}
                    placeholder="Select type..."
                  />
                )}

                <div className="flex flex-row gap-3">
                  <div className="flex flex-col gap-1.5 flex-1">
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">
                      Period From
                    </label>
                    <input
                      type="date"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      value={formData.periodFrom}
                      onChange={(e) => handleChange("periodFrom", e.target.value)}
                    />
                  </div>

                  <div className="flex flex-col gap-1.5 flex-1">
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">
                      Period To
                    </label>
                    <input
                      type="date"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      value={formData.periodTo}
                      onChange={(e) => handleChange("periodTo", e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 py-6 border-t border-gray-200 bg-gray-50 shrink-0 flex flex-col gap-3">

            <button
              type="button"
              onClick={() =>
                setFormData({
                  department: "",
                  status: "",
                  type: "",
                  periodFrom: "",
                  periodTo: "",
                })
              }
              className={clearButtonClasses}
              disabled={applying}
            >
              Clean All Filters
            </button>

            <div className="flex gap-3 w-full">
              <button
                type="button"
                onClick={handleClose}
                className={secondaryButtonClasses}
                disabled={applying}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApplyFilters}
                className={primaryButtonClasses}
                disabled={applying}
              >
                {applying ? "Applying..." : "Apply Filters"}
              </button>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
